import AppTableControl from '@/components/AppTableControl';
import AppPageTitle from '@/components/AppPageTitle';
import React, { lazy, Suspense, useState, useMemo, useEffect } from 'react';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getRegisterReport } from '@/services/revenue.service';
import dayjs from 'dayjs';
import { Button, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import useUniquePackages from '@/hooks/useUniquePackages';
import OverviewTable from './OverviewTable';
import OverviewChart from './OverviewChart';
import useRegisterData from './useRegisterData';
import AppLoading from '@/components/AppLoading';
import DetailTable from './DetailTable';
import ExcelExporter from '@/components/common/ExcelExporter';
import ChartMetricsSettings from '@/components/common/ChartMetricsSettings';

const selectOptions = [
  {
    value: 1,
    label: 'Sub Package',
  },
  {
    value: 2,
    label: 'Retail Package',
  },
];

const START_DATE = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function Register() {
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const [selectedFilter, setSelectedFilter] = useState(1);
  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getRegisterReport({
        start_date: date[0],
        end_date: date[1],
        packageOption: selectedFilter,
      }),
    ['register-revenue', date, selectedFilter]
  );

  const chartMetrics =
    selectedFilter === 1
      ? [
        { key: 'register_success', type: 'bar', label: 'Successful Registers', color: '#6a9eeb' },
        { key: 'renew_success', type: 'bar', label: 'Successful Renews', color: 'red' },
        { key: 'cancel_total', type: 'bar', label: 'Total Cancels', color: 'orange' },
      ]
      : [
        { key: 'total', type: 'bar', label: 'Total Registers', color: '#6a9eeb' },
        { key: 'success', type: 'bar', label: 'Successful Registers', color: 'red' },
      ];

  const [visibleMetrics, setVisibleMetrics] = useState(chartMetrics.map((metric) => metric.key));

  useEffect(() => {
    if (chartMetrics) {
      setVisibleMetrics(chartMetrics.map((metric) => metric.key));
    }
  }, [selectedFilter]);
  const uniquePackages = useUniquePackages(selectedFilter);

  // Use the updated hook that returns { data, totals, dataWithTotals }
  const registerResult = useRegisterData(data, uniquePackages, selectedFilter);
  const formatData = registerResult.data;
  const overviewTotals = registerResult.totals;
  const overviewDataForExport = registerResult.dataWithTotals;


  const columnMappingPackage = () => {
    return selectedFilter === 1
      ? {
        register_total: 'Total Register',
        register_success: 'Success Register',
        register_fail: 'Fail Register',
        register_rate: 'Rate Register',
        renew_total: 'Total Renew',
        renew_success: 'Success Renew',
        renew_fail: 'Fail Renew',
        renew_rate: 'Rate Renew',
        cancel_total: 'Total Cancel',
        cancel_user_total: 'User Cancel',
        cancel_sys_total: 'System Cancel',
      }
      : {
        total: 'Total',
        success: 'Success',
        fail: 'Fail',
        rate: 'Rate',
      };
  };

  const createOverviewGroupedHeaders = (packageOption) => {
    const groups = [];

    if (packageOption === 1) {
      // Sub package structure
      groups.push({
        title: 'Register',
        children: [{ title: 'Total' }, { title: 'Success' }, { title: 'Fail' }, { title: 'Rate' }],
      });

      groups.push({
        title: 'Renew',
        children: [{ title: 'Total' }, { title: 'Success' }, { title: 'Fail' }, { title: 'Rate' }],
      });

      groups.push({
        title: 'Cancel',
        children: [{ title: 'Total' }, { title: 'User' }, { title: 'System' }],
      });
    } else {
      // Retail package structure - single level headers
      groups.push({ title: 'Total' });
      groups.push({ title: 'Success' });
      groups.push({ title: 'Fail' });
      groups.push({ title: 'Rate' });
    }

    return groups;
  };

  return (
    <div className="flex flex-col items-start">
      <AppPageTitle
        title="Register"
        extra={
          <Button onClick={refetch} disabled={isLoading || isRefetching} icon={<SyncOutlined />}>
            Sync
          </Button>
        }
      />
      <AppTableControl
        selectOptions={selectOptions}
        selectValue={selectedFilter}
        setSelectValue={setSelectedFilter}
        date={date}
        setDate={setDate}
        dateLimit={{
          value: 30,
          unit: 'day',
        }}
        noStyle
      />

      <div className="flex items-center justify-between w-full mt-6">
        <h2 className="text-2xl font-medium ">Overview</h2>
        <div className="flex items-center gap-1">
          <ExcelExporter
            data={overviewDataForExport} // Use data with totals from hook
            fileName={`register_overview_${date[0]}_${date[1]}_${selectedFilter === 1 ? 'sub' : 'retail'}.xlsx`}
            sheetName={`Register Overview`}
            columnMapping={{
              package_code: 'Package',
              ...columnMappingPackage(),
            }}
            groupedHeaders={createOverviewGroupedHeaders(selectedFilter)}
            totalRowIndex={overviewDataForExport.length - 1} // Last row is totals
            tooltip={`Export ${selectedFilter === 1 ? 'Sub Package' : 'Retail Package'} Overview`}
          />
          <ChartMetricsSettings
            chartMetrics={chartMetrics}
            disabled={isLoading || isRefetching}
            title="Chart Metrics Settings"
            visibleMetrics={visibleMetrics}
            setVisibleMetrics={setVisibleMetrics}
          />
        </div>
      </div>

      <OverviewChart
        dataSource={formatData}
        packageOption={selectedFilter}
        loading={isLoading || isRefetching}
        visibleMetrics={visibleMetrics}
        chartMetrics={chartMetrics}
      />

      <OverviewTable
        dataSource={formatData}
        totals={overviewTotals}
        loading={isLoading || isRefetching}
        packageOption={selectedFilter}
      />
      <DetailTable
        dataSource={data}
        // dataLogRenew={data?.dataLogRenew}
        loading={isLoading || isRefetching}
        date={date}
        uniquePackages={uniquePackages}
        packageOption={selectedFilter}
      />
    </div>
  );
}

export default Register;
